import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate } from 'react-router-dom';
import { useMovieDetails } from '../hooks/useMovieDetails';
import MovieHero from '../components/movie-detail/MovieHero';
import MovieInfo from '../components/movie-detail/MovieInfo';
import { FiArrowLeft, FiRefreshCw } from 'react-icons/fi';
import { getMainTrailer } from '../utils/movieUtils';
import { urlFor } from '../utils/sanityClient';

const MovieDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { movie, loading, error } = useMovieDetails(slug || '');
  

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-16 bg-background-light">
        <div className="container-custom py-12">
          <div className="flex flex-col items-center justify-center">
            <div className="relative w-16 h-16">
              <div className="absolute inset-0 border-4 border-primary/20 rounded-full animate-spin" />
              <div className="absolute inset-0 border-t-4 border-primary rounded-full" />
            </div>
            <p className="mt-4 text-secondary-light">Loading movie details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !movie) {
    return (
      <div className="min-h-screen pt-16 bg-background-light">
        <div className="container-custom py-12">
          <div className="max-w-md mx-auto text-center">
            <h1 className="text-2xl font-heading font-bold text-secondary-dark mb-4">
              Oops! Something went wrong
            </h1>
            <p className="text-secondary-light mb-8">
              {error?.message || 'Movie not found'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleBack}
                className="btn-secondary inline-flex items-center justify-center gap-2"
              >
                <FiArrowLeft className="w-5 h-5" />
                Go Back
              </button>
              <button
                onClick={() => window.location.reload()}
                className="btn-primary inline-flex items-center justify-center gap-2"
              >
                <FiRefreshCw className="w-5 h-5" />
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-light">
      <Helmet>
        <title>{movie.title} | Next in Cinema</title>
        <meta name="description" content={movie.synopsis.substring(0, 160)} />
        
        {/* Open Graph tags */}
        <meta property="og:title" content={`${movie.title} | Next in Cinema`} />
        <meta property="og:description" content={movie.synopsis.substring(0, 160)} />
        <meta property="og:image" content={movie.poster} />
        <meta property="og:url" content={`https://www.nextincinema.com/movie/${movie.slug}`} />
        <meta property="og:type" content="video.movie" />
        <meta property="og:site_name" content="Next in Cinema" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${movie.title} | Next in Cinema`} />
        <meta name="twitter:description" content={movie.synopsis.substring(0, 160)} />
        <meta name="twitter:image" content={movie.poster} />
        
        {/* Additional SEO meta tags */}
        <meta name="keywords" content={`${movie.title}, movie, cinema, upcoming movie, film, trailer`} />
        <meta name="robots" content="index, follow" />
        
        {/* Canonical URL */}
        <link rel="canonical" href={`https://www.nextincinema.com/movie/${movie.slug}`} />
        
        {/* Schema.org structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Movie",
            "name": movie.title,
            "description": movie.synopsis,
            "image": movie.poster,
            "datePublished": movie.releaseDate,
            "director": movie.directors && movie.directors.length > 0
              ? movie.directors.map(director => ({
                  "@type": "Person",
                  "name": director.name
                }))
              : movie.director && movie.director.name
                ? {
                    "@type": "Person",
                    "name": movie.director.name
                  }
                : undefined,
            "actor": movie.mainActors && movie.mainActors.length > 0
              ? movie.mainActors.map(actor => ({
                  "@type": "Person",
                  "name": actor.name
                }))
              : [],
            "url": `https://www.nextincinema.com/movie/${movie.slug}`,
            ...(getMainTrailer(movie.trailers) && {
              "trailer": {
                "@type": "VideoObject",
                "name": `${movie.title} Trailer`,
                "description": `Watch the trailer for ${movie.title}`,
                "thumbnailUrl": movie.poster ? urlFor(movie.poster) : undefined,
                "uploadDate": movie.releaseDate
                  ? (movie.releaseDate.includes('T') ? movie.releaseDate : `${movie.releaseDate}T00:00:00Z`)
                  : undefined,
                "embedUrl": getMainTrailer(movie.trailers)?.videoUrl
              }
            })
          })}
        </script>
      </Helmet>
      
      <button
        onClick={handleBack}
        className="fixed top-20 left-4 z-50 bg-white/80 backdrop-blur-sm hover:bg-white text-secondary-dark p-2 rounded-full shadow-lg transition-colors duration-200"
      >
        <FiArrowLeft className="w-6 h-6" />
      </button>

      <MovieHero
        id={movie._id} // Pass the movie _id
        title={movie.title}
        releaseDate={movie.releaseDate}
        hypeLevel={movie.hypeLevel}
        poster={movie.poster}
        posterCopyright={movie.posterCopyright}
        synopsis={movie.synopsis}
        synopsisCopyright={movie.synopsisCopyright}
        trailer={getMainTrailer(movie.trailers)}
      />

      <MovieInfo
        director={movie.director}
        directors={movie.directors || []}
        mainActors={movie.mainActors}
        trailers={movie.trailers || []}
      />
    </div>
  );
};

export default MovieDetail;